import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Text, theme } from '@hhgtech/hhg-components'
import dayjs from 'dayjs'
import { useIntl } from 'react-intl'

import CalendarSimpleIcon from '@/public/svg/calendar-simple.svg'
import ThreeDotsIcon from '@/public/svg/three-dots-info.svg'
import {
  Calendar,
  CalendarMode,
  CalendarProps,
} from 'components/molecules/calendar'
import { HealthToolMorePopup } from 'components/molecules/healthToolMorePopup'
import { CalendarModal } from 'components/organisms/calendarModal'
import { useDateUtils } from 'hooks/useDateUtils'
import { useIsMobile } from 'hooks/useIsMobile'
import { useOnClickOutside } from 'hooks/useOnClickOutsite'
import {
  OVUL_CALC_TARGET_AVOID_PREGNANCY,
  OVUL_CALC_TARGET_TRACK_PERIOD,
  PREGNANCY_RATE_BY_OVUL_DIFF,
} from 'interfaces/constants'
import {
  CalendarMarker,
  CalendarRange,
  CalendarRangeType,
} from 'interfaces/types'

import {
  StyledCardHeadCalButton,
  StyledOvulationCalculatorCard,
  StyledOvulationCalculatorCardBody,
  StyledOvulationCalculatorCardBodyButton,
  StyledOvulationCalculatorCardBodyDetails,
  StyledOvulationCalculatorCardBodyEditButton,
  StyledOvulationCalculatorCardBodyTitle1,
  StyledOvulationCalculatorCardBodyTitle2,
  StyledOvulationCalculatorCardCalTooltip,
  StyledOvulationCalculatorCardFoot,
  StyledOvulationCalculatorCardFootItem,
  StyledOvulationCalculatorCardFootItemBadge,
  StyledOvulationCalculatorCardFootItemLabel,
  StyledOvulationCalculatorCardHead,
} from './index.styled'

export type OvulationCalculatorCardProps = {
  calcObjective: 1 | 2 | 3
  className?: string
  startDate?: Date
  selectedDate: Date
  calendarRanges?: CalendarRange[]
  ovulationDays?: Date[]
  ovulationMarkers?: CalendarMarker[]
  onChangeStartDate?: (startDate: Date) => void
  onChangeSelectedDate?: (selectedDate: Date) => void
  onClickRecalculate: () => void
}

export function OvulationCalculatorCard({
  calcObjective,
  className,
  startDate = new Date(),
  selectedDate,
  calendarRanges = [],
  ovulationDays = [],
  ovulationMarkers = [],
  onChangeStartDate,
  onChangeSelectedDate,
  onClickRecalculate,
}: OvulationCalculatorCardProps) {
  const { formatMessage: f } = useIntl()
  const isMobile = useIsMobile()
  const { localize } = useDateUtils()

  const [isCalendarModalOpen, setIsCalendarModalOpen] = useState(false)
  const [isCalendarTooltipOpen, setIsCalendarTooltipOpen] = useState(false)
  const calendarRef = useRef<HTMLDivElement | null>(null)
  const [morePopupShown, setMorePopupShown] = useState<boolean>(false)

  useOnClickOutside(calendarRef, () => {
    setIsCalendarTooltipOpen(false)
    if (onChangeStartDate) {
      onChangeStartDate(selectedDate)
    }
  })

  useEffect(() => {
    if (!isCalendarModalOpen) {
      window.scrollTo(0, 0)
    }
  }, [isCalendarModalOpen])

  const calButtonLabel = useMemo(() => {
    if (!localize) {
      return null
    }
    let date = startDate
    if (
      isCalendarTooltipOpen &&
      dayjs(selectedDate).isBetween(startDate, dayjs(startDate).add(2, 'month'))
    ) {
      date = selectedDate
    }
    return `${localize.month(date.getMonth(), {
      width: 'abbreviated',
    })} ${date.getFullYear()}`
  }, [localize, startDate, selectedDate, isCalendarTooltipOpen, f, isMobile])

  // const monthAndDay = useMemo(() => {
  //   if (!localize || !selectedDate) {
  //     return null
  //   }
  //   return `${localize.month(
  //     selectedDate.getMonth(),
  //   )}, ${selectedDate.getDate()}`
  // }, [localize, selectedDate])

  const getNextPeriodRange = useCallback(
    (d: Date) =>
      calendarRanges
        .filter(
          (r) => r.rangeType === 'period' && dayjs(r.startDate).isAfter(d),
        )
        .sort((a, b) => a.startDate.getTime() - b.startDate.getTime())[0],
    [calendarRanges],
  )

  const getInPeriodRange = useCallback(
    (d: Date) =>
      calendarRanges.find(
        (r) =>
          r.rangeType === 'period' &&
          dayjs(d).isBetween(
            dayjs(r.startDate).subtract(1, 'day'),
            dayjs(r.endDate).add(1, 'day'),
            'day',
          ),
      ),
    [calendarRanges],
  )

  const getInFertileRange = useCallback(
    (d: Date) =>
      calendarRanges.find(
        (r) =>
          r.rangeType === 'fertile' &&
          dayjs(d).isBetween(r.startDate, r.endDate),
      ),
    [calendarRanges],
  )

  const getClosestOvulationDay = useCallback(
    (d: Date) => {
      const d2 = dayjs(d)
      return ovulationDays.sort(
        (a, b) => Math.abs(d2.diff(a, 'day')) - Math.abs(d2.diff(b, 'day')),
      )[0]
    },
    [ovulationDays],
  )

  const getIsOvulationDay = useCallback(
    (date: Date) => {
      return ovulationDays.some((d) => dayjs(d).isSame(date, 'date'))
    },
    [ovulationDays],
  )

  const getInRange = useCallback<
    (
      inPeriodRange: CalendarRange | undefined,
      inFertileRange: CalendarRange | undefined,
    ) => CalendarRangeType
  >((inPeriodRange, inFertileRange) => {
    if (inPeriodRange) {
      return 'period'
    }
    if (inFertileRange) {
      return 'fertile'
    }
    return 'none'
  }, [])

  function handleCalendarButtonClick() {
    if (isMobile) {
      setIsCalendarModalOpen(true)
    } else {
      setIsCalendarTooltipOpen(true)
    }
  }

  function handleCalendarModalClose() {
    setIsCalendarModalOpen(false)
    if (onChangeStartDate) {
      onChangeStartDate(selectedDate)
    }
  }

  function renderBodyTitle1(d: Date) {
    const inPeriodRange = getInPeriodRange(d)
    const nextPeriodRange = getNextPeriodRange(d)
    const isOvulationDay = getIsOvulationDay(d)
    const closestOvulationDay = getClosestOvulationDay(d)
    const inFertileRange = getInFertileRange(d)
    if (inPeriodRange) {
      return f({ id: 'health.ovulationCalculator.period' })
    }
    if (calcObjective === OVUL_CALC_TARGET_TRACK_PERIOD) {
      if (nextPeriodRange) {
        return f({ id: 'health.ovulationCalculator.periodIn' })
      }
    } else {
      if (isOvulationDay) {
        return f({ id: 'health.ovulationCalculator.dayOf' })
      } else if (closestOvulationDay && inFertileRange) {
        const diff = dayjs(closestOvulationDay).diff(d, 'day')
        if (diff > 0) {
          return f({ id: 'health.ovulationCalculator.ovulationIn' })
        } else {
          return f({ id: 'health.ovulationCalculator.periodIn' })
        }
      }
    }
    if (nextPeriodRange && closestOvulationDay) {
      const diff = dayjs(closestOvulationDay).diff(d, 'day')
      if (diff < 0) {
        return f({ id: 'health.ovulationCalculator.periodIn' })
      } else {
        return f({ id: 'health.ovulationCalculator.ovulationIn' })
      }
    }
    return null
  }

  function renderBodyTitle2(d: Date) {
    const inPeriodRange = getInPeriodRange(d)
    const nextPeriodRange = getNextPeriodRange(d)
    const isOvulationDay = getIsOvulationDay(d)
    const closestOvulationDay = getClosestOvulationDay(d)
    const inFertileRange = getInFertileRange(d)
    if (inPeriodRange) {
      return f(
        { id: 'common.day.singular' },
        {
          day: dayjs(d).diff(inPeriodRange.startDate, 'day') + 1,
        },
      )
    }
    let diff
    if (calcObjective === OVUL_CALC_TARGET_TRACK_PERIOD) {
      if (nextPeriodRange) {
        diff = dayjs(nextPeriodRange.startDate).diff(d, 'day')
      }
    } else {
      if (isOvulationDay) {
        return f({ id: 'health.ovulationCalculator.ovulation' })
      } else if (inFertileRange) {
        const diffOvulation = dayjs(closestOvulationDay).diff(d, 'day')
        if (diffOvulation > 0) {
          diff = diffOvulation
        } else if (nextPeriodRange) {
          diff = dayjs(nextPeriodRange.startDate).diff(d, 'day')
        }
      }
    }
    if (!diff) {
      if (nextPeriodRange && closestOvulationDay) {
        const diffOvulation = dayjs(closestOvulationDay).diff(d, 'day')
        if (diffOvulation < 0) {
          diff = dayjs(nextPeriodRange.startDate).diff(d, 'day')
        } else {
          diff = diffOvulation
        }
      }
    }
    if (diff) {
      return f(
        {
          id: `common.day.${diff === 1 ? 'singular' : 'plural'}`,
        },
        { day: diff },
      )
    }
    return null
  }

  function renderBodyDetails(d: Date) {
    const inPeriodRange = getInPeriodRange(d)
    const closestOvulationDay = getClosestOvulationDay(d)
    if (calcObjective === OVUL_CALC_TARGET_AVOID_PREGNANCY) {
      if (inPeriodRange) {
        return (
          <span className="low-risk">
            {f({ id: 'ovulationPage.chanceVeryLow' })}
          </span>
        )
      }
      if (closestOvulationDay) {
        const diff = dayjs(closestOvulationDay).diff(d, 'day')
        if (diff === 0) {
          return (
            <span className="risky">
              {f({ id: 'ovulationPage.chanceVeryRisky' })}
            </span>
          )
        }
        if (diff >= -1 && diff <= 5) {
          return (
            <span className="risky">
              {f({ id: 'ovulationPage.chanceRisky' })}
            </span>
          )
        }
      }
      return (
        <span className="low-risk">{f({ id: 'ovulationPage.chanceLow' })}</span>
      )
    }
    let rate = 2.1
    if (inPeriodRange) {
      rate = 1
    } else if (closestOvulationDay) {
      const diff = dayjs(closestOvulationDay).diff(d, 'day')
      if (diff >= -1 && diff <= 5) {
        rate = PREGNANCY_RATE_BY_OVUL_DIFF[diff]
      }
    }
    return (
      <span>
        {f({ id: 'ovulationPage.pregnancyRate' })}{' '}
        <span className="rate highlighted">{rate}%</span>
      </span>
    )
  }

  function renderCalendar(
    mode: CalendarMode,
    props?: Omit<CalendarProps, 'mode'>,
  ) {
    return (
      <Calendar
        mode={mode}
        start={startDate}
        selected={selectedDate}
        onChangeStart={onChangeStartDate}
        onChangeSelected={onChangeSelectedDate}
        ranges={calendarRanges}
        markers={ovulationMarkers}
        renderTooltip={mode === 'monthly' ? renderTooltip : undefined}
        {...props}
      />
    )
  }

  function renderTooltip(d: Date) {
    return (
      <>
        <Text size="p2" color={theme.colors.gray800} type="bold">
          {renderBodyTitle1(d)} {renderBodyTitle2(d)}
        </Text>
        <Text size="p3">{renderBodyDetails(d)}</Text>
      </>
    )
  }

  return (
    <StyledOvulationCalculatorCard
      className={className}
      data-in-range={getInRange(
        getInPeriodRange(selectedDate),
        getInFertileRange(selectedDate),
      )}
      data-avoid-pregnancy={calcObjective === OVUL_CALC_TARGET_AVOID_PREGNANCY}
    >
      <div>
        {isCalendarTooltipOpen && (
          <StyledOvulationCalculatorCardCalTooltip ref={calendarRef}>
            {renderCalendar('monthly')}
          </StyledOvulationCalculatorCardCalTooltip>
        )}
      </div>
      <StyledOvulationCalculatorCardHead>
        <StyledCardHeadCalButton
          data-active={isCalendarTooltipOpen}
          onClick={handleCalendarButtonClick}
        >
          <CalendarSimpleIcon />
          {calButtonLabel}
        </StyledCardHeadCalButton>
      </StyledOvulationCalculatorCardHead>
      {renderCalendar('weekly', { className: 'onboarding-calendar' })}
      <StyledOvulationCalculatorCardBody>
        <StyledOvulationCalculatorCardBodyEditButton
          onClick={() => setMorePopupShown(true)}
        >
          <ThreeDotsIcon />
        </StyledOvulationCalculatorCardBodyEditButton>

        {morePopupShown && (
          <HealthToolMorePopup
            isMobile={isMobile}
            shareUrl={window?.location?.href ?? ''}
            onRecalculate={onClickRecalculate}
            onClose={() => setMorePopupShown(false)}
          />
        )}

        <StyledOvulationCalculatorCardBodyTitle1 className="highlighted">
          {renderBodyTitle1(selectedDate)}
        </StyledOvulationCalculatorCardBodyTitle1>
        <StyledOvulationCalculatorCardBodyTitle2>
          {renderBodyTitle2(selectedDate)}
        </StyledOvulationCalculatorCardBodyTitle2>
        <StyledOvulationCalculatorCardBodyDetails>
          {renderBodyDetails(selectedDate)}
        </StyledOvulationCalculatorCardBodyDetails>
        <StyledOvulationCalculatorCardBodyButton
          onClick={onClickRecalculate}
          color="tertiary"
          size="md"
        >
          {f({ id: 'health.ovulationCalculator.editPeriodDate' })}
        </StyledOvulationCalculatorCardBodyButton>
      </StyledOvulationCalculatorCardBody>
      <StyledOvulationCalculatorCardFoot>
        <StyledOvulationCalculatorCardFootItem>
          <StyledOvulationCalculatorCardFootItemBadge className="badge--period" />
          <StyledOvulationCalculatorCardFootItemLabel>
            {f({ id: 'health.ovulationCalculator.period' })}
          </StyledOvulationCalculatorCardFootItemLabel>
        </StyledOvulationCalculatorCardFootItem>
        {calcObjective !== OVUL_CALC_TARGET_TRACK_PERIOD && (
          <>
            <StyledOvulationCalculatorCardFootItem>
              <StyledOvulationCalculatorCardFootItemBadge className="badge--fertile" />
              <StyledOvulationCalculatorCardFootItemLabel>
                {f({ id: 'health.ovulationCalculator.fertile' })}
              </StyledOvulationCalculatorCardFootItemLabel>
            </StyledOvulationCalculatorCardFootItem>
            <StyledOvulationCalculatorCardFootItem>
              <StyledOvulationCalculatorCardFootItemBadge className="badge--ovulation" />
              <StyledOvulationCalculatorCardFootItemLabel>
                {f({ id: 'health.ovulationCalculator.ovulation' })}
              </StyledOvulationCalculatorCardFootItemLabel>
            </StyledOvulationCalculatorCardFootItem>
          </>
        )}
      </StyledOvulationCalculatorCardFoot>
      <div>
        {isCalendarModalOpen && (
          <CalendarModal
            onClose={handleCalendarModalClose}
            startDate={startDate}
            ranges={calendarRanges}
            markers={ovulationMarkers}
            selectedDate={selectedDate}
            onChangeSelectedDate={onChangeSelectedDate}
            onChangeStartDate={onChangeStartDate}
            renderTooltip={renderTooltip}
          />
        )}
      </div>
    </StyledOvulationCalculatorCard>
  )
}
